import React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InputIcon from '@mui/icons-material/Input';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OutputIcon from '@mui/icons-material/Output';
import PsychologyIcon from '@mui/icons-material/Psychology';
import RefreshIcon from '@mui/icons-material/Refresh';

import { DEFAULT_QUERY_OPTIONS } from '../../../../../support/constants';
import { apiGet } from '../../../../../support/aiApi';
import { boolChip, normalizeItems, safePreview } from '../TuningAdminTable';

import {
  countWords,
  deploymentLabel,
  docVersionLabel,
  getPromptInputs,
  getRagChunks,
  getResponseMetadata,
  markdownFromListItems,
  statusChip,
  stringifyForMarkdown,
  versionLabel,
  versionSourceLabel,
} from './runDiagnosticsUtils';

import {
  DetailField,
  DiagnosticAccordion,
  DiagnosticCard,
  JsonTree,
  MarkdownBox,
  RagChunksList,
  WrappedText,
} from './RunDisplay';

import {
  currentGenerationRunIdOf,
  generationRunHasValidation,
  getCurrentGenerationRun,
  rootGenerationRunIdOf,
} from './generationRunValidationState';

type GenerationDetailFocus =
  | 'overview'
  | 'db-context'
  | 'rag'
  | 'llm-request'
  | 'llm-response'
  | 'validation'
  | 'questions'
  | 'raw';

type Props = {
  open: boolean;
  run: any | null;
  onClose: () => void;
  onValidate: (run: any) => void;
  onRevalidate?: (run: any) => void;
  validating?: boolean;
  focus?: GenerationDetailFocus;
  onFocusChange?: (focus: GenerationDetailFocus) => void;
};

const focusToTab: Record<GenerationDetailFocus, number> = {
  overview: 0,
  'db-context': 1,
  rag: 2,
  'llm-request': 3,
  'llm-response': 4,
  validation: 5,
  questions: 6,
  raw: 7,
};

const tabToFocus: Record<number, GenerationDetailFocus> = {
  0: 'overview',
  1: 'db-context',
  2: 'rag',
  3: 'llm-request',
  4: 'llm-response',
  5: 'validation',
  6: 'questions',
  7: 'raw',
};

function dateLabel(value?: string | null) {
  if (!value) return '-';

  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch {
    return String(value);
  }
}

function scoreLabel(value: any, threshold?: any) {
  if (value === null || value === undefined || value === '') return '-';
  if (threshold === null || threshold === undefined || threshold === '') return String(value);
  return `${value} / ${threshold}`;
}

function validationRunTitle(row: any) {
  return row?.id || row?.validation_run_id || '-';
}

function questionResultTitle(row: any) {
  return row?.question_key || row?.key || row?.id || '-';
}

function normalizeStatus(value: any) {
  return String(value || '').trim().toLowerCase();
}

function isRunningStatus(value: any) {
  const status = normalizeStatus(value);

  return [
    'pending',
    'queued',
    'created',
    'running',
    'processing',
    'validating',
    'regenerating',
    'in_progress',
  ].includes(status);
}

function isTerminalValidationStatus(value: any) {
  const status = normalizeStatus(value);

  return [
    'passed',
    'publishable',
    'failed',
    'needs_review',
    'error',
    'validation_failed',
    'validation_passed',
    'validation_error',
  ].includes(status);
}

function validationRunIsTerminal(row: any) {
  if (!row) return false;
  if (row.evaluated_at || row.completed_at) return true;
  return isTerminalValidationStatus(row.status);
}

function generationRunIsRunning(row: any) {
  if (!row) return false;
  return isRunningStatus(row.status);
}

function runIdOf(value: any) {
  return (
    currentGenerationRunIdOf(value) ||
    getCurrentGenerationRun(value)?.id ||
    value?.id ||
    null
  );
}

function getGroupedGenerationRuns(row: any): any[] {
  if (!row) return [];

  if (Array.isArray(row.generation_runs) && row.generation_runs.length) {
    return row.generation_runs;
  }

  if (
    Array.isArray(row.current_generation_run?.generation_runs) &&
    row.current_generation_run.generation_runs.length
  ) {
    return row.current_generation_run.generation_runs;
  }

  const current = getCurrentGenerationRun(row);
  return current ? [current] : [];
}

function mergeRowsById(primary: any[], secondary: any[]) {
  const byId = new Map<string, any>();

  [...primary, ...secondary].forEach((item) => {
    if (!item?.id) return;
    byId.set(String(item.id), item);
  });

  return Array.from(byId.values());
}

const GenerationRunDetailDialog: React.FC<Props> = ({
                                                      open,
                                                      run,
                                                      onClose,
                                                      onValidate,
                                                      onRevalidate,
                                                      validating,
                                                      focus = 'overview',
                                                      onFocusChange,
                                                    }) => {
  const [tab, setTab] = React.useState(0);
  const [selectedValidationRun, setSelectedValidationRun] = React.useState<any | null>(null);
  const [selectedQuestionResult, setSelectedQuestionResult] = React.useState<any | null>(null);

  const previousOpenRef = React.useRef(false);
  const previousRunIdRef = React.useRef<any>(null);

  const currentRunFromGroup = React.useMemo(() => getCurrentGenerationRun(run), [run]);
  const effectiveRunId = runIdOf(run);

  React.useEffect(() => {
    const wasOpen = previousOpenRef.current;
    const previousRunId = previousRunIdRef.current;

    previousOpenRef.current = open;
    previousRunIdRef.current = effectiveRunId;

    if (!open) return;

    const openedNow = !wasOpen;
    const changedRun = previousRunId !== effectiveRunId;

    if (openedNow || changedRun) {
      setTab(focusToTab[focus] ?? 0);
    }
  }, [open, focus, effectiveRunId]);

  const generationRunQuery = useQuery<any>({
    queryKey: ['ai-generation-run', 'detail', effectiveRunId],
    queryFn: () =>
      apiGet<any>(`db/ai-generation-run/${effectiveRunId}/`, {
        detailed: true,
        depth: 3,
      }),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(open && effectiveRunId),
    placeholderData: keepPreviousData,
    refetchInterval: open && validating ? 3000 : false,
  });

  const effectiveRun = generationRunQuery.data || currentRunFromGroup || run;

  const validationRunsQuery = useQuery<any>({
    queryKey: ['ai-validation-run', 'by-generation-run', effectiveRun?.id],
    queryFn: () =>
      apiGet<any>('db/ai-validation-run/', {
        generation_run: effectiveRun?.id,
        size: 100,
        detailed: true,
        depth: 2,
        'order-by': '-created_at',
      }),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(open && effectiveRun?.id),
    placeholderData: keepPreviousData,
    refetchInterval: open && validating ? 3000 : false,
  });

  const queriedValidationRuns = normalizeItems(validationRunsQuery.data);

  const groupedValidationRuns = React.useMemo(() => {
    if (Array.isArray(run?.validation_runs)) return run.validation_runs;
    if (Array.isArray(run?.current_generation_run?.validation_runs)) {
      return run.current_generation_run.validation_runs;
    }
    return [];
  }, [run]);

  const validationRuns = React.useMemo(() => {
    return mergeRowsById(queriedValidationRuns, groupedValidationRuns).sort((a, b) => {
      const aTime = new Date(a.created_at || a.created || a.evaluated_at || 0).getTime();
      const bTime = new Date(b.created_at || b.created || b.evaluated_at || 0).getTime();
      return bTime - aTime;
    });
  }, [queriedValidationRuns, groupedValidationRuns]);

  React.useEffect(() => {
    if (!open) return;

    if (!validationRuns.length) {
      setSelectedValidationRun(null);
      setSelectedQuestionResult(null);
      return;
    }

    const stillExists = selectedValidationRun?.id
      ? validationRuns.some((item) => String(item.id) === String(selectedValidationRun.id))
      : false;

    if (!stillExists) {
      setSelectedValidationRun(validationRuns[0]);
      setSelectedQuestionResult(null);
    }
  }, [open, validationRuns, selectedValidationRun?.id]);

  const questionResultsQuery = useQuery<any>({
    queryKey: ['ai-validation-question-result', 'by-validation-run', selectedValidationRun?.id],
    queryFn: () =>
      apiGet<any>('db/ai-validation-question-result/', {
        validation_run: selectedValidationRun?.id,
        size: 250,
        detailed: true,
        depth: 2,
        'order-by': 'question_key',
      }),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(open && selectedValidationRun?.id),
    placeholderData: keepPreviousData,
    refetchInterval: open && validating ? 3000 : false,
  });

  const questionResults = normalizeItems(questionResultsQuery.data);

  React.useEffect(() => {
    if (!open) return;

    if (!questionResults.length) {
      setSelectedQuestionResult(null);
      return;
    }

    const stillExists = selectedQuestionResult?.id
      ? questionResults.some((item) => String(item.id) === String(selectedQuestionResult.id))
      : false;

    if (!stillExists) {
      setSelectedQuestionResult(questionResults[0]);
    }
  }, [open, questionResults, selectedQuestionResult?.id]);

  const refetchAll = async () => {
    await generationRunQuery.refetch();
    await validationRunsQuery.refetch();

    if (selectedValidationRun?.id) {
      await questionResultsQuery.refetch();
    }
  };

  const changeTab = (_event: React.SyntheticEvent, value: number) => {
    setTab(value);
    onFocusChange?.(tabToFocus[value] || 'overview');
  };

  const selectValidationRun = (row: any) => {
    setSelectedValidationRun(row);
    setSelectedQuestionResult(null);
    setTab(5);
    onFocusChange?.('validation');
  };

  const selectQuestionResultsValidationRun = (validationRunId: string) => {
    const nextValidationRun =
      validationRuns.find((item) => String(item.id) === String(validationRunId)) || null;

    setSelectedValidationRun(nextValidationRun);
    setSelectedQuestionResult(null);
    setTab(6);
    onFocusChange?.('questions');
  };

  const selectQuestionResult = (row: any) => {
    setSelectedQuestionResult(row);
  };

  if (!effectiveRun) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>Generation Run Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            No generation run selected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const promptInputs = getPromptInputs(effectiveRun);
  const responseMetadata = getResponseMetadata(effectiveRun);
  const ragChunks = getRagChunks(effectiveRun);
  const groupedGenerationRuns = getGroupedGenerationRuns(run);

  const promptCharCount = String(effectiveRun.prompt_text || '').length;
  const promptWordCount = countWords(effectiveRun.prompt_text);
  const responseCharCount = String(effectiveRun.response_text || '').length;
  const responseWordCount = countWords(effectiveRun.response_text);
  const ragContextCharCount = String(effectiveRun.rag_context || '').length;

  const profileId =
    effectiveRun.profile ||
    effectiveRun.profile_id ||
    promptInputs.profile_id ||
    promptInputs.profile?.id;

  const requiredResource = promptInputs.required_resource || {};
  const resourceSpec = promptInputs.resource_spec || {};
  const versionContext = promptInputs.version_context || {};

  const latestValidationRun = validationRuns[0] || null;
  const failedQuestions = questionResults.filter((row) => row.passed === false);
  const blockingQuestions = questionResults.filter((row) => row.is_blocking);
  const passedQuestions = questionResults.filter((row) => row.passed === true);

  const hasExistingValidation =
    validationRuns.length > 0 ||
    generationRunHasValidation(effectiveRun) ||
    generationRunHasValidation(run);

  const latestValidationTerminal = validationRunIsTerminal(latestValidationRun);
  const latestValidationRunning = latestValidationRun
    ? isRunningStatus(latestValidationRun.status)
    : false;

  const generationStillRunning = generationRunIsRunning(effectiveRun);

  const effectiveValidating = Boolean(
    (validating || latestValidationRunning || generationStillRunning) &&
    !latestValidationTerminal,
  );

  const validationActionLoading =
    validationRunsQuery.isLoading &&
    !generationRunHasValidation(effectiveRun) &&
    !generationRunHasValidation(run);

  const isInitialLoading =
    generationRunQuery.isLoading && !generationRunQuery.data && !currentRunFromGroup;

  const isRefreshing =
    !isInitialLoading &&
    (generationRunQuery.isFetching ||
      validationRunsQuery.isFetching ||
      questionResultsQuery.isFetching);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Generation Run Diagnostics
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {effectiveRun.entity_type}:{effectiveRun.entity_id} /{' '}
              {effectiveRun.resource_type || effectiveRun.section_key || '-'} / Run {effectiveRun.id}
            </Typography>

            {rootGenerationRunIdOf(run || effectiveRun) ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ overflowWrap: 'anywhere' }}
              >
                Root: {rootGenerationRunIdOf(run || effectiveRun)} / Current: {effectiveRun.id}
              </Typography>
            ) : null}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {statusChip(effectiveRun.status)}
            <Chip size="small" variant="outlined" label={`Model: ${effectiveRun.model_name || '-'}`} />
            <Chip size="small" variant="outlined" label={`RAG: ${effectiveRun.rag_provider || '-'}`} />
            <Chip size="small" variant="outlined" label={`Response: ${responseWordCount} words`} />
            <Chip
              size="small"
              variant="outlined"
              color={validationRuns.length ? 'primary' : 'default'}
              label={`${validationRuns.length} validation run(s)`}
            />
            {groupedGenerationRuns.length > 1 ? (
              <Chip
                size="small"
                variant="outlined"
                color="secondary"
                label={`${groupedGenerationRuns.length} generation attempt(s)`}
              />
            ) : null}
            {effectiveValidating ? (
              <Chip size="small" color="warning" variant="outlined" label="Running..." />
            ) : null}
            {isRefreshing ? (
              <Chip size="small" variant="outlined" label="Refreshing..." />
            ) : null}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {isInitialLoading ? <LinearProgress /> : null}

          {effectiveRun.error_message ? (
            <Alert severity="error">
              {effectiveRun.error_message}
            </Alert>
          ) : null}

          {effectiveValidating ? (
            <Alert severity="info">
              Validation/regeneration is running. The modal refreshes in the background without resetting the selected tab.
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <DetailField label="Status" value={statusChip(effectiveRun.status)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Run ID" value={effectiveRun.id} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Entity" value={`${effectiveRun.entity_type}:${effectiveRun.entity_id}`} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Resource / Section" value={effectiveRun.resource_type || effectiveRun.section_key || '-'} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Model" value={effectiveRun.model_name || '-'} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Limiter" value={effectiveRun.limiter_key || '-'} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Profile" value={profileId || '-'} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Created" value={dateLabel(effectiveRun.created_at)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Product Version" value={versionLabel(effectiveRun)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Doc Version" value={docVersionLabel(effectiveRun)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Deployment Scope" value={deploymentLabel(effectiveRun)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <DetailField label="Version Source" value={versionSourceLabel(effectiveRun)} />
              </Grid>
            </Grid>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Unified Run Review
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Review generation context, RAG evidence, LLM request/response, validation runs, question results, and raw records.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon fontSize="small" />}
                  onClick={refetchAll}
                >
                  Refresh
                </Button>

                {hasExistingValidation ? (
                  <Button
                    size="small"
                    color="secondary"
                    variant="contained"
                    startIcon={<AutorenewIcon fontSize="small" />}
                    disabled={!effectiveRun.response_text || effectiveValidating || validationActionLoading || !onRevalidate}
                    onClick={() => onRevalidate?.(run || effectiveRun)}
                  >
                    {validationActionLoading ? 'Checking...' : 'Regenerate & Revalidate'}
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<FactCheckIcon fontSize="small" />}
                    disabled={!effectiveRun.response_text || effectiveValidating || validationActionLoading}
                    onClick={() => onValidate(run || effectiveRun)}
                  >
                    {validationActionLoading ? 'Checking...' : 'Validate & Improve'}
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Diagnostic Flow
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={3}>
                <DiagnosticCard
                  icon={<DataObjectIcon color="primary" fontSize="small" />}
                  title="1. DB/Profile Context"
                  subtitle="Profile, flags, entity/resource relationship, and resource specs."
                  status={<Chip size="small" variant="outlined" label={profileId ? 'profile found' : 'profile unknown'} />}
                >
                  <Typography variant="caption" color="text.secondary">
                    Use this when model, prompt flags, or resource metadata look wrong.
                  </Typography>
                </DiagnosticCard>
              </Grid>

              <Grid item xs={12} md={3}>
                <DiagnosticCard
                  icon={<ManageSearchIcon color="primary" fontSize="small" />}
                  title="2. RAG Retrieval"
                  subtitle="RAG query, chunks, and rendered context captured for this run."
                  status={
                    <Chip
                      size="small"
                      variant="outlined"
                      color={effectiveRun.rag_query ? 'success' : 'default'}
                      label={effectiveRun.rag_query ? 'query present' : 'no query'}
                    />
                  }
                >
                  <Typography variant="caption" color="text.secondary">
                    Use this when generated content is missing facts or source grounding.
                  </Typography>
                </DiagnosticCard>
              </Grid>

              <Grid item xs={12} md={3}>
                <DiagnosticCard
                  icon={<PsychologyIcon color="primary" fontSize="small" />}
                  title="3. LLM Request / Response"
                  subtitle="Final prompt and generated response are separate explicit tabs."
                  status={
                    <Chip
                      size="small"
                      variant="outlined"
                      color={effectiveRun.response_text ? 'success' : 'default'}
                      label={effectiveRun.response_text ? 'response present' : 'no response'}
                    />
                  }
                >
                  <Typography variant="caption" color="text.secondary">
                    The response panel is display-only and no longer toggles to the request view.
                  </Typography>
                </DiagnosticCard>
              </Grid>

              <Grid item xs={12} md={3}>
                <DiagnosticCard
                  icon={<FactCheckIcon color="primary" fontSize="small" />}
                  title="4. Validation"
                  subtitle="Validation score, failure context, and question-level evidence."
                  status={
                    <Chip
                      size="small"
                      variant="outlined"
                      color={latestValidationRun?.is_publishable ? 'success' : latestValidationRun ? 'warning' : 'default'}
                      label={
                        latestValidationRun
                          ? `latest score ${latestValidationRun.score ?? '-'}`
                          : 'not validated'
                      }
                    />
                  }
                >
                  <Typography variant="caption" color="text.secondary">
                    Use this after validate/revalidate to understand pass/fail behavior.
                  </Typography>
                </DiagnosticCard>
              </Grid>
            </Grid>
          </Paper>

          <Tabs
            value={tab}
            onChange={changeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Flow Overview" />
            <Tab label="DB Context" />
            <Tab label="RAG Query & Response" />
            <Tab label="LLM Request" />
            <Tab label="LLM Response" />
            <Tab label="Validation Runs" />
            <Tab label="Question Results" />
            <Tab label="Raw Run Record" />
          </Tabs>

          {tab === 0 ? (
            <Stack spacing={1.5}>
              <DiagnosticAccordion
                defaultExpanded
                icon={<DataObjectIcon color="primary" />}
                title="1. Database-derived generation context"
                subtitle="Profile, entity, resource relationship, resource spec, scope, and version context."
                chips={
                  <>
                    <Chip size="small" variant="outlined" label={`Profile: ${profileId || '-'}`} />
                    <Chip size="small" variant="outlined" label={`Resource: ${effectiveRun.resource_type || effectiveRun.section_key || '-'}`} />
                  </>
                }
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <JsonTree
                      value={{
                        profile_id: profileId,
                        model_name: effectiveRun.model_name,
                        limiter_key: effectiveRun.limiter_key,
                        rag_provider: effectiveRun.rag_provider,
                        entity_type: effectiveRun.entity_type,
                        entity_id: effectiveRun.entity_id,
                        entity_name: effectiveRun.entity_name,
                        entity_description: effectiveRun.entity_description,
                        context_name: effectiveRun.context_name,
                        resource_type: effectiveRun.resource_type,
                        section_key: effectiveRun.section_key,
                        section_title: effectiveRun.section_title,
                      }}
                      maxHeight={300}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <JsonTree
                      value={{
                        required_resource: requiredResource,
                        resource_spec: resourceSpec,
                        depth_key: promptInputs.depth_key,
                        version_context: versionContext,
                        scope_spec_entity_type: promptInputs.scope_spec_entity_type,
                        high_level_guidance_entity_type: promptInputs.high_level_guidance_entity_type,
                        authoritative_usage_key: promptInputs.authoritative_usage_key,
                        product_reference_key: promptInputs.product_reference_key,
                      }}
                      maxHeight={300}
                    />
                  </Grid>
                </Grid>
              </DiagnosticAccordion>

              <DiagnosticAccordion
                icon={<ManageSearchIcon color="primary" />}
                title="2. RAG query and retrieved context"
                subtitle="Collapsed by default to avoid rendering large context unless needed."
                chips={
                  <>
                    <Chip size="small" variant="outlined" label={`${ragChunks.length} chunks`} />
                    <Chip size="small" variant="outlined" label={`${ragContextCharCount} chars`} />
                  </>
                }
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={5}>
                    <WrappedText label="RAG Query" maxHeight={220}>
                      {effectiveRun.rag_query || '-'}
                    </WrappedText>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <RagChunksList chunks={ragChunks} />
                  </Grid>

                  <Grid item xs={12}>
                    <MarkdownBox label="Rendered RAG Context Sent Into Prompt" maxHeight={360}>
                      {effectiveRun.rag_context || stringifyForMarkdown(effectiveRun.rag_context_json)}
                    </MarkdownBox>
                  </Grid>
                </Grid>
              </DiagnosticAccordion>

              <DiagnosticAccordion
                icon={<InputIcon color="primary" />}
                title="3. Final LLM request"
                subtitle="Final assembled prompt and prompt inputs."
                chips={
                  <>
                    <Chip size="small" variant="outlined" label={`${promptWordCount} words`} />
                    <Chip size="small" variant="outlined" label={`${promptCharCount} chars`} />
                  </>
                }
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} lg={7}>
                    <MarkdownBox label="Final Prompt Text" maxHeight={520}>
                      {effectiveRun.prompt_text || ''}
                    </MarkdownBox>
                  </Grid>

                  <Grid item xs={12} lg={5}>
                    <WrappedText label="Prompt Inputs JSON" maxHeight={520} copyValue={promptInputs}>
                      {JSON.stringify(promptInputs, null, 2)}
                    </WrappedText>
                  </Grid>
                </Grid>
              </DiagnosticAccordion>

              <DiagnosticAccordion
                icon={<OutputIcon color="primary" />}
                title="4. LLM response"
                subtitle="Raw response saved for this generation run."
                chips={
                  <>
                    <Chip size="small" variant="outlined" label={`${responseWordCount} words`} />
                    <Chip size="small" variant="outlined" label={`${responseCharCount} chars`} />
                  </>
                }
              >
                <MarkdownBox label="Generated Content" maxHeight={560}>
                  {effectiveRun.response_text || ''}
                </MarkdownBox>
              </DiagnosticAccordion>

              <DiagnosticAccordion
                defaultExpanded={Boolean(latestValidationRun)}
                icon={<FactCheckIcon color="primary" />}
                title="5. Validation summary"
                subtitle="Latest validation score, pass/fail state, and question-result counts."
                chips={
                  <>
                    <Chip size="small" variant="outlined" label={`${validationRuns.length} validation run(s)`} />
                    <Chip size="small" variant="outlined" color="success" label={`${passedQuestions.length} passed`} />
                    <Chip size="small" variant="outlined" color="error" label={`${failedQuestions.length} failed`} />
                    <Chip size="small" variant="outlined" color="warning" label={`${blockingQuestions.length} blocking`} />
                  </>
                }
              >
                {latestValidationRun ? (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={3}>
                      <DetailField label="Latest Status" value={statusChip(latestValidationRun.status)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DetailField
                        label="Latest Score"
                        value={scoreLabel(latestValidationRun.score, latestValidationRun.publishable_threshold)}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DetailField label="Publishable" value={boolChip(latestValidationRun.is_publishable)} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DetailField label="Blocking Failure" value={boolChip(latestValidationRun.blocking_failure)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MarkdownBox label="Failure Summary" maxHeight={260}>
                        {latestValidationRun.failure_summary || ''}
                      </MarkdownBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MarkdownBox label="Regeneration Context" maxHeight={260}>
                        {latestValidationRun.regeneration_context || ''}
                      </MarkdownBox>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No validation runs are linked to this generation run yet.
                  </Typography>
                )}
              </DiagnosticAccordion>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <WrappedText label="Prompt Inputs JSON" maxHeight={720} copyValue={promptInputs}>
                  {JSON.stringify(promptInputs, null, 2)}
                </WrappedText>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <WrappedText label="Required Resource Relationship" maxHeight={220} copyValue={requiredResource}>
                    {JSON.stringify(requiredResource, null, 2)}
                  </WrappedText>

                  <WrappedText label="Resource / Section Spec" maxHeight={220} copyValue={resourceSpec}>
                    {JSON.stringify(resourceSpec, null, 2)}
                  </WrappedText>

                  <WrappedText label="Version Context" maxHeight={220} copyValue={versionContext}>
                    {JSON.stringify(versionContext, null, 2)}
                  </WrappedText>
                </Stack>
              </Grid>
            </Grid>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <DetailField label="RAG Provider" value={effectiveRun.rag_provider || '-'} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField label="RAG Query Present" value={effectiveRun.rag_query ? 'yes' : 'no'} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DetailField label="Retrieved Chunks" value={ragChunks.length} />
                </Grid>
              </Grid>

              <WrappedText label="RAG Query" maxHeight={220}>
                {effectiveRun.rag_query || '-'}
              </WrappedText>

              <RagChunksList chunks={ragChunks} />

              <MarkdownBox label="Rendered RAG Context" maxHeight={620}>
                {effectiveRun.rag_context || stringifyForMarkdown(effectiveRun.rag_context_json)}
              </MarkdownBox>
            </Stack>
          ) : null}

          {tab === 3 ? (
            <Grid container spacing={1.5}>
              <Grid item xs={12} lg={8}>
                <MarkdownBox label="Final Prompt Sent to LLM" maxHeight={760}>
                  {effectiveRun.prompt_text || ''}
                </MarkdownBox>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Stack spacing={1.5}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <DetailField label="Prompt Words" value={promptWordCount} />
                    </Grid>
                    <Grid item xs={6}>
                      <DetailField label="Prompt Chars" value={promptCharCount} />
                    </Grid>
                    <Grid item xs={6}>
                      <DetailField label="Model" value={effectiveRun.model_name || '-'} />
                    </Grid>
                    <Grid item xs={6}>
                      <DetailField label="Limiter" value={effectiveRun.limiter_key || '-'} />
                    </Grid>
                  </Grid>

                  <WrappedText label="Prompt Inputs JSON" maxHeight={560} copyValue={promptInputs}>
                    {JSON.stringify(promptInputs, null, 2)}
                  </WrappedText>
                </Stack>
              </Grid>
            </Grid>
          ) : null}

          {tab === 4 ? (
            <Stack spacing={2}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={3}>
                  <DetailField label="Response Words" value={responseWordCount} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DetailField label="Response Chars" value={responseCharCount} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DetailField label="Completed" value={dateLabel(effectiveRun.completed_at)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DetailField label="Status" value={statusChip(effectiveRun.status)} />
                </Grid>
              </Grid>

              <MarkdownBox label="Generated Content" maxHeight={760}>
                {effectiveRun.response_text || ''}
              </MarkdownBox>

              <WrappedText label="Response Metadata" maxHeight={260} copyValue={responseMetadata}>
                {JSON.stringify(responseMetadata, null, 2)}
              </WrappedText>
            </Stack>
          ) : null}

          {tab === 5 ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Validation Runs for This Generation Run
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Select a validation run to inspect its details. Use Question Results for question-level reasoning.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={`${validationRuns.length} total`} />
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label={`${validationRuns.filter((row) => row.is_publishable).length} publishable`}
                    />
                    <Chip
                      size="small"
                      color="error"
                      variant="outlined"
                      label={`${validationRuns.filter((row) => row.blocking_failure).length} blocking failure`}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1050 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Validation Run</TableCell>
                      <TableCell width={150}>Status</TableCell>
                      <TableCell width={140}>Score</TableCell>
                      <TableCell width={120}>Publishable</TableCell>
                      <TableCell width={120}>Blocking</TableCell>
                      <TableCell>Failure Summary</TableCell>
                      <TableCell width={190}>Created</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {validationRuns.map((row) => {
                      const selected = String(selectedValidationRun?.id) === String(row.id);

                      return (
                        <TableRow
                          key={String(row.id)}
                          hover
                          selected={selected}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => selectValidationRun(row)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                              {validationRunTitle(row)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                              Generation: {row.generation_run || row.generation_run_id || effectiveRun.id}
                            </Typography>
                          </TableCell>

                          <TableCell>{statusChip(row.status)}</TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {scoreLabel(row.score, row.publishable_threshold)}
                            </Typography>
                          </TableCell>

                          <TableCell>{boolChip(row.is_publishable)}</TableCell>

                          <TableCell>{boolChip(row.blocking_failure)}</TableCell>

                          <TableCell>
                            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                              {safePreview(row.failure_summary || row.regeneration_context, 260)}
                            </Typography>
                          </TableCell>

                          <TableCell>{dateLabel(row.created_at || row.created)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {!validationRuns.length ? (
                <Typography variant="body2" color="text.secondary">
                  No validation runs are linked to this generation run yet.
                </Typography>
              ) : null}

              {selectedValidationRun ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Status" value={statusChip(selectedValidationRun.status)} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Validation Run ID" value={selectedValidationRun.id} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Generation Run ID" value={selectedValidationRun.generation_run || selectedValidationRun.generation_run_id || '-'} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField
                          label="Score"
                          value={scoreLabel(selectedValidationRun.score, selectedValidationRun.publishable_threshold)}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Publishable" value={boolChip(selectedValidationRun.is_publishable)} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Blocking Failure" value={boolChip(selectedValidationRun.blocking_failure)} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Threshold" value={selectedValidationRun.publishable_threshold ?? '-'} />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <DetailField label="Needs Review" value={selectedValidationRun.needs_review_threshold ?? '-'} />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid item xs={12} lg={6}>
                        <MarkdownBox label="Failure Summary" maxHeight={300}>
                          {selectedValidationRun.failure_summary || ''}
                        </MarkdownBox>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <MarkdownBox label="Attribution" maxHeight={300}>
                          {selectedValidationRun.attribution_summary || ''}
                        </MarkdownBox>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <MarkdownBox label="Regeneration Context" maxHeight={360}>
                          {selectedValidationRun.regeneration_context || ''}
                        </MarkdownBox>
                      </Grid>

                      <Grid item xs={12} lg={6}>
                        <MarkdownBox label="Raw Validator Response" maxHeight={360}>
                          {stringifyForMarkdown(selectedValidationRun.raw_validator_response)}
                        </MarkdownBox>
                      </Grid>
                    </Grid>

                    {selectedValidationRun.error_message ? (
                      <Alert severity="error">{selectedValidationRun.error_message}</Alert>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}
            </Stack>
          ) : null}

          {tab === 6 ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', md: 'flex-start' }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        Validation Question Results
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Question results are shown for the selected validation run.
                      </Typography>

                      {selectedValidationRun ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.5,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          Selected Validation Run: {selectedValidationRun.id}
                        </Typography>
                      ) : null}
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                      <Chip size="small" variant="outlined" label={`${questionResults.length} total`} />
                      <Chip size="small" color="success" variant="outlined" label={`${passedQuestions.length} passed`} />
                      <Chip size="small" color="error" variant="outlined" label={`${failedQuestions.length} failed`} />
                      <Chip size="small" color="warning" variant="outlined" label={`${blockingQuestions.length} blocking`} />
                    </Stack>
                  </Stack>

                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Validation Run"
                    value={selectedValidationRun?.id || ''}
                    disabled={!validationRuns.length}
                    onChange={(event) => selectQuestionResultsValidationRun(event.target.value)}
                    helperText={
                      validationRuns.length
                        ? 'Select a validation run to view its question-level results.'
                        : 'No validation runs are available for this generation run.'
                    }
                  >
                    {validationRuns.map((validationRun, index) => (
                      <MenuItem key={String(validationRun.id)} value={String(validationRun.id)}>
                        {index === 0 ? 'Latest — ' : ''}
                        {validationRun.id}
                        {' / '}
                        Status: {validationRun.status || 'unknown'}
                        {' / '}
                        Score: {validationRun.score ?? '-'}
                        {' / '}
                        Created: {dateLabel(validationRun.created_at || validationRun.created)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Paper>

              {!selectedValidationRun ? (
                <Alert severity="info">
                  Select a validation run to view question-level results.
                </Alert>
              ) : null}

              {selectedValidationRun ? (
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Question Result List
                    </Typography>

                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                      <Table
                        size="small"
                        sx={{
                          minWidth: 900,
                          tableLayout: 'fixed',
                          '& td, & th': {
                            verticalAlign: 'top',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Question</TableCell>
                            <TableCell width={110}>Score</TableCell>
                            <TableCell width={110}>Passed</TableCell>
                            <TableCell width={120}>Blocking</TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {questionResults.map((row) => {
                            const selected = String(selectedQuestionResult?.id) === String(row.id);

                            return (
                              <TableRow
                                key={String(row.id)}
                                hover
                                selected={selected}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => selectQuestionResult(row)}
                              >
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                  >
                                    {questionResultTitle(row)}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      display: 'block',
                                      overflowWrap: 'anywhere',
                                      wordBreak: 'break-word',
                                      whiteSpace: 'normal',
                                    }}
                                  >
                                    {safePreview(row.question_text || row.question, 260)}
                                  </Typography>
                                </TableCell>

                                <TableCell>{row.score ?? '-'}</TableCell>
                                <TableCell>{boolChip(row.passed)}</TableCell>
                                <TableCell>{boolChip(row.is_blocking)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>

                    {!questionResults.length ? (
                      <Typography variant="body2" color="text.secondary" mt={2}>
                        No question results were returned for this validation run.
                      </Typography>
                    ) : null}
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {selectedQuestionResult ? (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          Selected Question Result Detail
                        </Typography>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={3}>
                            <DetailField label="Question" value={selectedQuestionResult.question_key || '-'} />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <DetailField label="Score" value={selectedQuestionResult.score ?? '-'} />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <DetailField label="Passed" value={boolChip(selectedQuestionResult.passed)} />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <DetailField label="Blocking" value={boolChip(selectedQuestionResult.is_blocking)} />
                          </Grid>
                        </Grid>

                        <MarkdownBox label="Question Text" maxHeight={220}>
                          {selectedQuestionResult.question_text || selectedQuestionResult.question || ''}
                        </MarkdownBox>

                        <Grid container spacing={2}>
                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Reasoning" maxHeight={340}>
                              {selectedQuestionResult.reasoning || ''}
                            </MarkdownBox>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Missing Evidence" maxHeight={340}>
                              {selectedQuestionResult.missing_evidence || ''}
                            </MarkdownBox>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Recommended Fix" maxHeight={380}>
                              {selectedQuestionResult.recommended_fix || ''}
                            </MarkdownBox>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Regeneration Context" maxHeight={380}>
                              {selectedQuestionResult.regeneration_context || ''}
                            </MarkdownBox>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Unsupported Claims" maxHeight={420}>
                              {markdownFromListItems(selectedQuestionResult.unsupported_claims)}
                            </MarkdownBox>
                          </Grid>

                          <Grid item xs={12} lg={6}>
                            <MarkdownBox label="Supporting Evidence" maxHeight={420}>
                              {markdownFromListItems(selectedQuestionResult.supporting_evidence)}
                            </MarkdownBox>
                          </Grid>
                        </Grid>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Select a question result to inspect reasoning, evidence, recommended fixes, unsupported claims, and regeneration context.
                      </Typography>
                    )}
                  </Paper>
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          {tab === 7 ? (
            <Stack spacing={2}>
              <WrappedText label="Full Raw Generation Run Object" maxHeight={520} copyValue={effectiveRun}>
                {JSON.stringify(effectiveRun, null, 2)}
              </WrappedText>

              <WrappedText label="Grouped Generation Runs" maxHeight={360} copyValue={groupedGenerationRuns}>
                {JSON.stringify(groupedGenerationRuns, null, 2)}
              </WrappedText>

              <WrappedText label="Validation Runs" maxHeight={360} copyValue={validationRuns}>
                {JSON.stringify(validationRuns, null, 2)}
              </WrappedText>

              <WrappedText label="Question Results for Selected Validation Run" maxHeight={360} copyValue={questionResults}>
                {JSON.stringify(questionResults, null, 2)}
              </WrappedText>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={refetchAll}
        >
          Refresh
        </Button>

        {hasExistingValidation ? (
          <Button
            size="small"
            color="secondary"
            variant="contained"
            startIcon={<AutorenewIcon fontSize="small" />}
            disabled={!effectiveRun.response_text || effectiveValidating || validationActionLoading || !onRevalidate}
            onClick={() => onRevalidate?.(run || effectiveRun)}
          >
            {validationActionLoading ? 'Checking...' : 'Regenerate & Revalidate'}
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={<FactCheckIcon fontSize="small" />}
            disabled={!effectiveRun.response_text || effectiveValidating || validationActionLoading}
            onClick={() => onValidate(run || effectiveRun)}
          >
            {validationActionLoading ? 'Checking...' : 'Validate & Improve'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerationRunDetailDialog;